import { useState } from "react";
import { Edit3Icon, RotateCcw, TrashIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "../../../Components/BreadCrumb";
import TableListing from "../../../Components/TableListing";
import { Button } from "../../../Components/Buttons";
import { ToastMessage } from "../../../Components/Utils";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { handleError } from "../../../stores/components/handleError";
import {
  useFetchBrandsQuery,
  useFetchTrashedBrandsQuery,
  useDeleteBrandMutation,
  useDestroyBrandMutation,
  useRestoreBrandMutation,
  useBulkDeleteBrandsMutation,
  useBulkDestroyBrandsMutation,
  useBulkRestoreBrandsMutation,
} from "../../../stores/api/brands.api";
import payloadHelper from "../../../stores/api/helpers/payload.helper";
import { brand_image_url } from "../../../Components/ImageUrls";

const EMPTY_PAGINATION = {
  data: [],
  current_page: 1,
  total_pages: 1,
  total_items: 0,
  current_items: 0,
  limit: 10,
};

const HEADERS = [
  { key: "image", label: "Image", sortable:false,type: "image" , imageBaseUrl: brand_image_url },
  { key: "name", label: "Name" },
  {
    key: "status",
    label: "Status",
    type: "status",
  },
];


const BrandList = () => {
  const navigate = useNavigate();
  const [trashed, setTrashed] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const queryParams = payloadHelper.forList({
    page,
    limit,
    search,
    sortBy: sortConfig.key,
    sortOrder: sortConfig.direction,
  });

  const activeQuery = useFetchBrandsQuery(queryParams, { skip: trashed });
  const trashedQuery = useFetchTrashedBrandsQuery(queryParams, { skip: !trashed });
  const { data: brands = EMPTY_PAGINATION, isFetching } = trashed ? trashedQuery : activeQuery;

  const [deleteBrand] = useDeleteBrandMutation();
  const [destroyBrand] = useDestroyBrandMutation();
  const [restoreBrand] = useRestoreBrandMutation();
  const [bulkDeleteBrands] = useBulkDeleteBrandsMutation();
  const [bulkDestroyBrands] = useBulkDestroyBrandsMutation();
  const [bulkRestoreBrands] = useBulkRestoreBrandsMutation();

  const resetPage = () => setPage(1);
  
  const handleSingle = (row, type = "delete") => {
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: `${verb} Brand`,
      message: `${verb} "${row.name}"?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          if (type === "restore") await restoreBrand(row.id).unwrap();
          else if (type === "destroy") await destroyBrand(row.id).unwrap();
          else await deleteBrand(row.id).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `Brand ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed.");
        }
      },
    });
  };

  const handleBulk = (selectedItems, type = "delete") => {
    const ids = selectedItems;
    const verb = type === "restore" ? "Restore" : type === "destroy" ? "Destroy" : "Delete";
    ToastMessage({
      type: "warning",
      title: "Bulk Action",
      message: `${verb} ${ids.length} brands?`,
      toastType: "confirm",
      onYes: async () => {
        try {
          const body = payloadHelper.forBulk(ids);
          if (type === "restore") await bulkRestoreBrands(body).unwrap();
          else if (type === "destroy") await bulkDestroyBrands(body).unwrap();
          else await bulkDeleteBrands(body).unwrap();
          ToastMessage({ type: "success", title: "Done!", message: `${ids.length} brands ${verb.toLowerCase()}d.` });
        } catch (err) {
          handleError(err, "Failed bulk action.");
        }
      },
    });
  };

  const handleTrashedChange = (value) => {
    setTrashed(value);
    resetPage();
  };

  return (
    <>
      <BreadCrumb title="Brands" />
      <TableListing
        headers={HEADERS}
        paginationData={brands}
        loading={isFetching}
        createButton
        createLink={ROUTE_PATHS.CREATE_BRAND}
        handleBulk={handleBulk}
        statusModule="brand"
        search={search}
        setSearch={setSearch}
        trashed={trashed}
        setTrashed={setTrashed}
        onTrashedChange={handleTrashedChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setLimit(value);
          resetPage();
        }}
        onSearch={(value) => {
          setSearch(value);
          resetPage();
        }}
        onSortChange={(sort) => {
          setSortConfig(sort);
          resetPage();
        }}
        actions={(row) => (
          <div className="flex gap-2">
            {!trashed ? (
              <Button
                onClick={() => navigate(ROUTE_PATHS.CREATE_BRAND, { state: { brand: row } })}
                title="Edit"
                icon={Edit3Icon}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            ) : (
              <Button
                onClick={() => handleSingle(row, "restore")}
                title="Restore"
                icon={RotateCcw}
                className="text-green-600 hover:bg-green-100 hover:text-green-700 dark:hover:bg-green-500/10"
              />
            )}
            <Button
              onClick={() => handleSingle(row, trashed ? "destroy" : "delete")}
              title="Delete"
              icon={TrashIcon}
              className="text-red-600 hover:bg-red-100 hover:text-red-700 dark:hover:bg-red-500/10"
            />
          </div>
        )}
      />
    </>
  );
};

export default BrandList;
